/**
 * 關鍵字提醒：哪些訊息值得先看。
 *
 * 兩個來源：
 *   1. **自己的暱稱**（自動）：訊息裡有人喊你，就該看到。
 *   2. **使用者自訂關鍵字**：存在 localStorage，重整／換分頁都還在。
 *
 * 純函式（`findHighlights` / `splitByHighlights` / `sortConversationsByHighlight`）沒有副作用、可單獨測；
 * 比對一律「不分大小寫、字面比對」（不是正規表達式，使用者打 `$` 不該爆掉）。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { compareConversations } from './useInbox';
/** localStorage 的鍵；版本號進鍵名，改格式時舊資料自然失效，不必寫遷移。 */
export const HIGHLIGHT_STORAGE_KEY = 'chat-rea.highlights.v1';
/** 自訂關鍵字上限：夠用又不至於讓清單被塞爆（比對成本是 O(關鍵字數)）。 */
export const MAX_CUSTOM_KEYWORDS = 20;
/** 單一關鍵字長度上限，避免貼一整段文章進去。 */
export const MAX_KEYWORD_LENGTH = 24;
/** 正規化：去頭尾空白、壓成單行、長度截斷。空字串代表「不成立」。 */
export function normalizeKeyword(raw) {
    return raw.replace(/\s+/g, ' ').trim().slice(0, MAX_KEYWORD_LENGTH);
}
/** 去重（不分大小寫）並保留原順序；已存在的字（含大小寫不同）不會再加一筆。 */
export function mergeKeywords(...lists) {
    const seen = new Set();
    const out = [];
    for (const list of lists) {
        for (const item of list ?? []) {
            const keyword = normalizeKeyword(item);
            if (keyword === '')
                continue;
            const key = keyword.toLowerCase();
            if (seen.has(key))
                continue;
            seen.add(key);
            out.push(keyword);
        }
    }
    return out;
}
/**
 * 文字裡命中了哪些關鍵字（去重、依出現位置排序）。
 * 空關鍵字一律忽略——否則每個字都會命中。
 */
export function findHighlights(text, keywords) {
    if (!text)
        return [];
    const haystack = text.toLowerCase();
    const hits = [];
    const seen = new Set();
    for (const raw of keywords) {
        const keyword = normalizeKeyword(raw);
        if (keyword === '')
            continue;
        const key = keyword.toLowerCase();
        if (seen.has(key))
            continue;
        const at = haystack.indexOf(key);
        if (at === -1)
            continue;
        seen.add(key);
        hits.push({ keyword, at });
    }
    return hits.sort((a, b) => a.at - b.at).map((hit) => hit.keyword);
}
/** 有沒有命中（給排序／標記用的快速版）。 */
export function hasHighlight(text, keywords) {
    return findHighlights(text, keywords).length > 0;
}
/**
 * 把文字切成「命中／未命中」片段，讓畫面能把關鍵字加粗而不用 dangerouslySetInnerHTML。
 * 重疊時取最靠左、同位置取最長的關鍵字（先比對長字，`小安` 不會把 `小安安` 切成兩段）。
 */
export function splitByHighlights(text, keywords) {
    if (!text)
        return [];
    const ordered = mergeKeywords(keywords).sort((a, b) => b.length - a.length);
    if (ordered.length === 0)
        return [{ text, hit: false }];
    const lower = text.toLowerCase();
    const segments = [];
    let cursor = 0;
    while (cursor < text.length) {
        let bestAt = -1;
        let bestLength = 0;
        for (const keyword of ordered) {
            const at = lower.indexOf(keyword.toLowerCase(), cursor);
            if (at === -1)
                continue;
            if (bestAt === -1 || at < bestAt || (at === bestAt && keyword.length > bestLength)) {
                bestAt = at;
                bestLength = keyword.length;
            }
        }
        if (bestAt === -1)
            break;
        if (bestAt > cursor)
            segments.push({ text: text.slice(cursor, bestAt), hit: false });
        segments.push({ text: text.slice(bestAt, bestAt + bestLength), hit: true });
        cursor = bestAt + bestLength;
    }
    if (cursor < text.length)
        segments.push({ text: text.slice(cursor), hit: false });
    return segments.length > 0 ? segments : [{ text, hit: false }];
}
/**
 * 排序：**命中且未讀**的對話置頂，其餘照原本規則（未讀多 → 最後活動新）。
 * 只有未讀的置頂才有意義——已讀的對話你已經看過了，再置頂只是擋路。
 */
export function sortConversationsByHighlight(conversations, isHit) {
    const weight = (conversation) => conversation.unread > 0 && isHit(conversation) ? 1 : 0;
    return [...conversations].sort((a, b) => {
        const diff = weight(b) - weight(a);
        return diff !== 0 ? diff : compareConversations(a, b);
    });
}
/** 從 localStorage 讀自訂關鍵字；壞資料（非陣列／含非字串）就當空的，不讓整頁爆掉。 */
export function readStoredKeywords(storageKey = HIGHLIGHT_STORAGE_KEY) {
    if (typeof window === 'undefined' || !window.localStorage)
        return [];
    try {
        const raw = window.localStorage.getItem(storageKey);
        if (!raw)
            return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed))
            return [];
        return mergeKeywords(parsed.filter((item) => typeof item === 'string')).slice(0, MAX_CUSTOM_KEYWORDS);
    }
    catch {
        return [];
    }
}
export function useHighlights(options = {}) {
    const { nicknames = [], storageKey = HIGHLIGHT_STORAGE_KEY } = options;
    const [custom, setCustom] = useState(() => readStoredKeywords(storageKey));
    // 只有自訂的部分要落地；暱稱每次從後端來，不寫進 storage（否則刪帳號後會留下死關鍵字）。
    useEffect(() => {
        if (typeof window === 'undefined' || !window.localStorage)
            return;
        try {
            window.localStorage.setItem(storageKey, JSON.stringify(custom));
        }
        catch {
            /* 無痕模式／配額滿：記不住就算了，不要讓 UI 掛掉 */
        }
    }, [custom, storageKey]);
    const nicknameKey = nicknames.join('\u0000');
    const nicknameList = useMemo(() => mergeKeywords(nicknameKey.split('\u0000').filter((item) => item !== '')), [nicknameKey]);
    const keywords = useMemo(() => mergeKeywords(nicknameList, custom), [nicknameList, custom]);
    const matches = useCallback((text) => findHighlights(text, keywords), [keywords]);
    const isHit = useCallback((text) => hasHighlight(text, keywords), [keywords]);
    const add = useCallback((keyword) => {
        const next = normalizeKeyword(keyword);
        if (next === '')
            return false;
        // 回傳值要同步可信，所以在 setState 之前就判斷；updater 只負責套用。
        const exists = custom.some((item) => item.toLowerCase() === next.toLowerCase());
        if (exists || custom.length >= MAX_CUSTOM_KEYWORDS)
            return false;
        setCustom((prev) => prev.some((item) => item.toLowerCase() === next.toLowerCase()) || prev.length >= MAX_CUSTOM_KEYWORDS
            ? prev
            : [...prev, next]);
        return true;
    }, [custom]);
    const remove = useCallback((keyword) => {
        const key = normalizeKeyword(keyword).toLowerCase();
        setCustom((prev) => prev.filter((item) => item.toLowerCase() !== key));
    }, []);
    /** 清單／對話串「取消提醒」用：按一下就把這個字從自訂清單拿掉（暱稱拿不掉）。 */
    const toggle = useCallback((text) => {
        const key = normalizeKeyword(text).toLowerCase();
        setCustom((prev) => prev.some((item) => item.toLowerCase() === key)
            ? prev.filter((item) => item.toLowerCase() !== key)
            : prev.length >= MAX_CUSTOM_KEYWORDS
                ? prev
                : [...prev, normalizeKeyword(text)]);
    }, []);
    const clearCustom = useCallback(() => setCustom([]), []);
    return { nicknames: nicknameList, custom, keywords, matches, isHit, add, remove, toggle, clearCustom };
}
//# sourceMappingURL=useHighlights.js.map